import React, { useState, useEffect, useCallback, useRef } from 'react';
import { updates } from './api';
import { formatBytes, formatDateTime, relativeTime } from './format';
import './Portal.css';

/**
 * Drag-and-drop deployment. One zip in, a summary and a rollback button out.
 */
export default function UpdatesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(-1);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [rollingBack, setRollingBack] = useState(null);
  const [confirmRollback, setConfirmRollback] = useState(null);
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const response = await updates.list();
      setData(response);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const deploy = async (file) => {
    if (!file) return;

    const looksLikeZip = /\.zip$/i.test(file.name) || file.type === 'application/zip';
    if (!looksLikeZip) {
      setError('Deployments must be a .zip file. You dropped "' + file.name + '".');
      return;
    }

    setError(null);
    setBlocked([]);
    setResult(null);
    setProgress(0);

    try {
      const response = await updates.apply(file, setProgress);
      setResult(response);
      load();
    } catch (err) {
      setError(err.message);
      if (err.data && err.data.blocked) {
        setBlocked(err.data.blocked);
      }
      if (err.data && err.data.skipped) {
        setBlocked(err.data.skipped);
      }
    } finally {
      setProgress(-1);
    }
  };

  const rollback = async (id) => {
    if (confirmRollback !== id) {
      setConfirmRollback(id);
      setTimeout(() => setConfirmRollback((current) => (current === id ? null : current)), 6000);
      return;
    }
    setConfirmRollback(null);
    setRollingBack(id);
    setError(null);
    setResult(null);
    try {
      const response = await updates.rollback(id);
      setResult(response);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setRollingBack(null);
    }
  };

  const caps = data ? data.capabilities : null;
  const busy = progress >= 0;

  return (
    <div>
      {caps && !caps.zip && (
        <div className="pt-notice pt-notice-warn" style={{ marginBottom: 18 }}>
          This server does not have PHP's <code>zip</code> extension enabled, so packages cannot be opened
          here. Enable "zip" in your hosting control panel, or deploy over FTP.
        </div>
      )}

      {caps && caps.zip && !caps.site_root_writable && (
        <div className="pt-notice pt-notice-warn" style={{ marginBottom: 18 }}>
          The web root is not writable by PHP, so front-end files cannot be replaced. Backend updates under{' '}
          <code>/api</code> {caps.api_dir_writable ? 'will still work.' : 'will also fail.'}
        </div>
      )}

      <section className="pt-section">
        <div
          className={'pt-drop' + (dragging ? ' is-dragging' : '')}
          onClick={() => !busy && fileInput.current && fileInput.current.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!busy) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (!busy) deploy(e.dataTransfer.files[0]);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !busy) {
              e.preventDefault();
              if (fileInput.current) fileInput.current.click();
            }
          }}
        >
          <div className="pt-drop-icon">
            <span role="img" aria-label="Package">
              📦
            </span>
          </div>
          <div className="pt-drop-title">{busy ? 'Deploying…' : 'Drop a deployment .zip here'}</div>
          <div className="pt-drop-sub">
            Drop in <code>northwoodshandy.zip</code> — the same package you upload by FTP.
            <br />
            Your photos, accounts and <code>config.php</code> are skipped automatically, never overwritten.
          </div>

          {busy && (
            <div className="pt-progress" style={{ maxWidth: 320, margin: '16px auto 0' }}>
              <div className="pt-progress-bar" style={{ width: progress + '%' }} />
            </div>
          )}

          <input
            ref={fileInput}
            type="file"
            accept=".zip,application/zip"
            style={{ display: 'none' }}
            onChange={(e) => {
              deploy(e.target.files[0]);
              e.target.value = '';
            }}
          />
        </div>

        {error && (
          <div className="pt-notice pt-notice-error" style={{ marginTop: 14 }}>
            <div>{error}</div>
            {blocked.length > 0 && (
              <ul style={{ margin: '10px 0 0', paddingLeft: 20 }}>
                {blocked.map((item, index) => (
                  <li key={index}>
                    <code>{item.path}</code> — {item.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {result && (
          <div
            className={'pt-notice pt-notice-' + (result.ok ? 'success' : 'warn')}
            style={{ marginTop: 14 }}
          >
            <div>{result.message}</div>
            {typeof result.written === 'number' && (
              <div style={{ marginTop: 8 }}>
                {result.frontend} front-end file(s), {result.backend} backend file(s) ·{' '}
                {result.replaced} replaced, {result.created} added
              </div>
            )}
            {result.skipped && result.skipped.length > 0 && (
              <>
                <div style={{ marginTop: 10, fontWeight: 700 }}>Skipped:</div>
                <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                  {result.skipped.map((item, index) => (
                    <li key={index}>
                      <code>{item.path}</code> — {item.reason}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {result.failures && result.failures.length > 0 && (
              <>
                <div style={{ marginTop: 10, fontWeight: 700 }}>Could not write:</div>
                <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                  {result.failures.map((path) => (
                    <li key={path}>
                      <code>{path}</code>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </section>

      <section className="pt-section">
        <h2 className="pt-section-title">What is protected</h2>
        <div className="pt-card">
          <p className="pt-page-sub" style={{ margin: 0 }}>
            These paths are skipped on every deployment, even when the package contains them — which is what
            lets one archive serve as both the FTP upload and a portal deployment. They are listed in the
            result summary so you can see they were left alone.
          </p>
          <ul style={{ margin: '12px 0 0', paddingLeft: 20, lineHeight: 1.8 }}>
            {(caps ? caps.protected_paths : ['api/data', 'api/uploads', 'api/config.php']).map((path) => (
              <li key={path}>
                <code>{path}</code>
                {path === 'api/data' && ' — accounts, logs, gallery records, rollback snapshots'}
                {path === 'api/uploads' && ' — every photo the owner has uploaded'}
                {path === 'api/config.php' && ' — your setup key, mail settings and site URL'}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="pt-section">
        <div className="pt-section-head">
          <h2 className="pt-section-title">Deployment history</h2>
          <button className="pt-btn pt-btn-ghost pt-btn-sm" type="button" onClick={load}>
            Refresh
          </button>
        </div>

        <div className="pt-card">
          {loading ? (
            <div className="pt-hint">Loading…</div>
          ) : !data || data.releases.length === 0 ? (
            <div className="pt-hint">
              Nothing has been deployed through the portal yet. The last{' '}
              {caps ? caps.keep_backups : 5} deployments keep a rollback snapshot.
            </div>
          ) : (
            <div className="pt-table-wrap">
              <table className="pt-table">
                <thead>
                  <tr>
                    <th>Package</th>
                    <th>Changes</th>
                    <th>When</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.releases.map((release) => (
                    <tr key={release.id}>
                      <td>
                        <strong>{release.filename}</strong>
                        <div className="pt-hint">
                          {formatBytes(release.bytes)} · {release.uploaded_by}
                        </div>
                      </td>
                      <td className="pt-hint">
                        {release.replaced_count} replaced, {release.created_count} added
                        <div>
                          {release.frontend} front end · {release.backend} backend
                        </div>
                      </td>
                      <td className="pt-hint" title={formatDateTime(release.uploaded_at)}>
                        {relativeTime(release.uploaded_at)}
                      </td>
                      <td>
                        <span
                          className={
                            'pt-pill pt-pill-' + (release.status === 'applied' ? 'ok' : 'muted')
                          }
                        >
                          <span className="pt-dot" />
                          {release.status === 'applied' ? 'live' : 'rolled back'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {release.can_rollback ? (
                          <button
                            className="pt-btn pt-btn-danger pt-btn-sm"
                            type="button"
                            onClick={() => rollback(release.id)}
                            disabled={rollingBack === release.id}
                          >
                            {rollingBack === release.id
                              ? 'Rolling back…'
                              : confirmRollback === release.id
                              ? 'Click again to confirm'
                              : 'Roll back'}
                          </button>
                        ) : (
                          <span className="pt-hint">
                            {release.status === 'applied' ? 'snapshot pruned' : ''}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
